/**
 * The member help line (TRUS-778): the number shown on the member door, the
 * sign-up screens and the marketing footer. A navigator answers texts and calls.
 *
 * The number comes from the backend (GET /api/member-portal/help-line): it is
 * the platform's default Twilio number, so dev and production each show their
 * own and nothing here hard-codes one. Until it loads, or when none is
 * configured, the hook returns null and callers leave the number out.
 *
 * Its own module (not lib/memberJoin.ts) so the public marketing pages can
 * show it without pulling the member-auth code into their bundle.
 */
import { useEffect, useState } from 'react';
import { getWeaveinApiUrl } from '@/src/config';

export interface MemberHelpLine {
  display: string;
  sms: string;
  tel: string;
}

let cached: MemberHelpLine | null | undefined;
let inflight: Promise<MemberHelpLine | null> | null = null;

export function toMemberHelpLine(data: unknown): MemberHelpLine | null {
  const d = data as { e164?: unknown; display?: unknown } | null;
  if (!d || typeof d.e164 !== 'string' || !d.e164.startsWith('+')) return null;
  const display = typeof d.display === 'string' && d.display ? d.display : d.e164;
  return { display, sms: `sms:${d.e164}`, tel: `tel:${d.e164}` };
}

export function fetchMemberHelpLine(): Promise<MemberHelpLine | null> {
  if (cached !== undefined) return Promise.resolve(cached);
  if (!inflight) {
    inflight = fetch(getWeaveinApiUrl('/api/member-portal/help-line'))
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => (cached = toMemberHelpLine(data)))
      .catch(() => null)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** The help line, or null while loading / when none is configured. */
export function useMemberHelpLine(): MemberHelpLine | null {
  const [line, setLine] = useState<MemberHelpLine | null>(cached ?? null);
  useEffect(() => {
    let live = true;
    fetchMemberHelpLine().then((l) => {
      if (live) setLine(l);
    });
    return () => {
      live = false;
    };
  }, []);
  return line;
}
